import classNames from "classnames";
import { parseISO } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, ListGroup, Modal } from "react-bootstrap";
import { Option } from "react-select/src/filters";
import { Icon, IconName } from "@/components";
import { PartnerBookingPolicy, PartnerBookingHour } from "@/data/partners";
import { parseTime, toDay } from "@/lib/datetime";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { Spacer } from "@/offering/spacer";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import { TimePicker } from "@/offering/time_picker";
import styles from "./mobile_filters.module.css";
import { addHours } from "@/offering/time_utils";
import { MeetingRoomDateRange } from "@/offering/order_params";
import { formatMeetingRoomDateRange } from "@/offering/format_date_range";
import {
  DateRangePicker,
  PickingRange,
} from "../date_range_picker/date_range_picker";

export enum FilterId {
  State = "state",
  City = "city",
  Location = "location",
  Capacity = "capacity",
  DateTime = "dateTime",
  Layout = "layout",
}

interface MobileFilterProps {
  label: string;
  iconName: IconName;
  options: Option[];
  value?: string;
  onChange: (value?: string) => void;
}

export type MobileFilter = {
  [key in FilterId]?: MobileFilterProps;
};

interface MobileFiltersProps {
  filters: MobileFilter;
  filterValues: MeetingRoomsFilter;
  onFilterValuesChange: (filter: MeetingRoomsFilter) => void;
  totalRooms: number;
  foundResults: number;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
}

export function MobileFilters({
  filters,
  filterValues,
  onFilterValuesChange,
  totalRooms,
  foundResults,
  partnerBookingHour,
  partnerBookingPolicy,
}: MobileFiltersProps) {
  const { fbt } = useI18n();

  const [showModal, setShowModal] = useState(false);
  const [internalFilterValues, setInternalFilterValues] =
    useState(filterValues);

  const locationOptions = useMemo(() => {
    let filteredLocations = filters.location?.options || [];
    if (internalFilterValues.state) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.state === internalFilterValues.state
      );
    }
    if (internalFilterValues.city) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.city === internalFilterValues.city
      );
    }
    return filteredLocations;
  }, [filters, internalFilterValues]);

  const cityOptions = useMemo(() => {
    let filteredCities = filters.city?.options || [];
    if (internalFilterValues.state) {
      filteredCities = filteredCities.filter(
        (o) => o.data.state === internalFilterValues.state
      );
    }
    return filteredCities;
  }, [filters, internalFilterValues]);

  const filterKeys = Object.keys(filters) as FilterId[];
  const numberOfFilters = filterKeys.length;

  useEffect(() => {
    if (numberOfFilters > 0) {
      setInternalFilterValues(filterValues);
    }
  }, [filterValues, numberOfFilters]);

  if (numberOfFilters === 0) {
    return <></>;
  }

  let content: JSX.Element;

  if (numberOfFilters < 3) {
    content = (
      <>
        {filters.state && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.state.value}
              onSelect={filters.state.onChange}
              options={filters.state.options}
              label={filters.state.label}
            />
          </div>
        )}
        {filters.city && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.city.value}
              onSelect={filters.city.onChange}
              options={filters.city.options}
              label={filters.city.label}
            />
          </div>
        )}
        {filters.location && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.location.value}
              onSelect={filters.location.onChange}
              options={filters.location.options}
              label={filters.location.label}
            />
          </div>
        )}
        {filters.layout && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.layout.value}
              onSelect={filters.layout.onChange}
              options={filters.layout.options}
              label={filters.layout.label}
            />
          </div>
        )}
        {filters.capacity && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.capacity.value}
              onSelect={filters.capacity.onChange}
              options={filters.capacity.options}
              label={filters.capacity.label}
            />
          </div>
        )}
        {filters.dateTime && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileDateTimeFilterItem
              value={filterValues}
              onChange={onFilterValuesChange}
              label={filters.dateTime.label}
              openAt={partnerBookingHour?.openAt}
              closedAt={partnerBookingHour?.closedAt}
              timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
            />
          </div>
        )}
      </>
    );
  } else {
    let numberOfAppliedFilter = 0;
    for (const key of filterKeys) {
      const filter = filters[key];
      if (filter?.value) {
        numberOfAppliedFilter++;
      }
    }

    let numberOfApplyingFilter = 0;
    const internalFilterValuesKeys = Object.keys(
      internalFilterValues
    ) as (keyof MeetingRoomsFilter)[];
    for (const key of internalFilterValuesKeys) {
      if (internalFilterValues[key]) {
        numberOfApplyingFilter++;
      }
    }

    const handleShowResults = () => {
      setShowModal(false);
      onFilterValuesChange(internalFilterValues);
    };

    const handleClearAll = () => {
      setInternalFilterValues((filters) => {
        const updated = { ...filters };
        for (const key of internalFilterValuesKeys) {
          updated[key] = undefined;
        }
        return updated;
      });
    };

    const roomsFoundTitle = fbt(
      fbt.plural("room", foundResults, {
        name: "numberOfRooms",
        showCount: "yes",
        many: "rooms",
      }) + " found"
    );

    content = (
      <>
        <div className={styles.filterResult}>
          {foundResults !== totalRooms && numberOfAppliedFilter > 0
            ? roomsFoundTitle
            : fbt("Showing all rooms")}
        </div>
        <button
          className={`${styles.filterBtn} gallery-mobile-filter-button`}
          onClick={() => setShowModal(true)}
        >
          {fbt("Filters")}
          {numberOfAppliedFilter > 0 ? ` (${numberOfAppliedFilter})` : ""}
        </button>

        <Modal
          show={showModal}
          dialogClassName={styles.modalDialog}
          contentClassName={styles.modalContent}
          onHide={() => setShowModal(false)}
          scrollable
          fullscreen
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <b>{fbt("Filters")}</b>
          </Modal.Header>
          <Modal.Body>
            <ListGroup className="mr-gallery-mobile-filters-modal-list-group">
              {filters.state && (
                <MobileModalFilterItem
                  value={internalFilterValues.state}
                  options={filters.state.options}
                  label={filters.state.label}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      state: value,
                      city: undefined,
                      locationID: undefined,
                    }))
                  }
                />
              )}
              {filters.city && (
                <MobileModalFilterItem
                  value={internalFilterValues.city}
                  options={cityOptions}
                  label={filters.city.label}
                  placeholder={fbt("Any city")}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      city: value,
                      locationID: undefined,
                    }))
                  }
                />
              )}
              {filters.location && (
                <MobileModalFilterItem
                  value={internalFilterValues.locationID}
                  options={locationOptions}
                  label={filters.location.label}
                  placeholder={fbt("Any location")}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      locationID: value,
                    }))
                  }
                />
              )}
              {filters.layout && (
                <MobileModalFilterItem
                  value={internalFilterValues.layoutId}
                  options={filters.layout.options}
                  label={filters.layout.label}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      layoutId: value,
                    }))
                  }
                />
              )}
              {filters.capacity && (
                <MobileModalFilterItem
                  value={internalFilterValues.minCapacity?.toString()}
                  options={filters.capacity.options}
                  label={filters.capacity.label}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      minCapacity: Number(value) || undefined,
                      maxCapacity:
                        filters.capacity?.options.find((o) => o.value === value)
                          ?.data.max || undefined,
                    }))
                  }
                />
              )}
              {filters.dateTime && (
                <MobileModalDateTimeFilterItem
                  value={internalFilterValues}
                  onChange={(dateTimeValue) =>
                    setInternalFilterValues((v) => ({ ...v, ...dateTimeValue }))
                  }
                  label={filters.dateTime.label}
                  openAt={partnerBookingHour?.openAt}
                  closedAt={partnerBookingHour?.closedAt}
                  timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
                />
              )}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <div className={styles.modalBtns}>
              <button
                className={styles.showResultsBtn}
                onClick={handleShowResults}
              >
                {fbt("Show results")}
              </button>
              {numberOfApplyingFilter > 0 && (
                <button className={styles.clearAllBtn} onClick={handleClearAll}>
                  {fbt("Clear all")}
                </button>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <div className={`${styles.container} mr-gallery-mobile-filter-container`}>
      {content}
    </div>
  );
}

interface MobileFilterItemProps {
  value?: string;
  label: string;
  onSelect: (value?: string) => void;
  options: Option[];
}

function MobileFilterItem({
  value,
  label,
  onSelect,
  options,
}: MobileFilterItemProps) {
  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.dropdown)}
        onSelect={(value) => onSelect(value || undefined)}
      >
        <Dropdown.Toggle>
          {value ? (
            <div className={styles.displayValue}>
              {options.find((o) => o.value === value)?.label}
            </div>
          ) : (
            <div className={styles.placeholder}>{label}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {value && (
          <div className={styles.clearBtn} onClick={() => onSelect(undefined)}>
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu className={styles.dropdownMenu}>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={option.value === value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

function MobileDateTimeFilterItem({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
}: MobileModalDateTimeFilterItemProps) {
  const { fbt, locale } = useI18n();
  const displayValue = formatMeetingRoomDateRange(value, fbt, locale, "");

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown className={classNames("checkout-box-picker", styles.dropdown)}>
        <Dropdown.Toggle>
          {displayValue ? (
            <div className={styles.displayValue}>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>{label}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {displayValue && (
          <div
            className={styles.clearBtn}
            onClick={() =>
              onChange({
                date: undefined,
                startTime: undefined,
                endTime: undefined,
              })
            }
          >
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu align="end">
          <div>
            <DateRangePicker
              pickingRange={PickingRange.OneDate}
              value={{
                startDate: value.date ? parseISO(value.date) : undefined,
              }}
              onChange={(value) =>
                onChange({
                  ...value,
                  date: toDay(value.startDate),
                })
              }
            />
            <div className="px-3 pb-3 d-flex">
              <TimePicker
                value={value.startTime}
                placeholder={fbt("Start time")}
                startTime={openAt}
                endTime={closedAt}
                step={timeSlotIncrement}
                onChange={(startTime) => onChange({ ...value, startTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.date}
              />
              <Spacer size={30} direction="horizontal" />
              <TimePicker
                value={value.endTime}
                step={timeSlotIncrement}
                placeholder={fbt("End time")}
                startTime={addHours(value.startTime || openAt || "0800", 1)}
                endTime={closedAt}
                onChange={(endTime) => onChange({ ...value, endTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.startTime}
              />
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface ModalFilterItemProps {
  onSelect: (value?: string) => void;
  value?: string;
  placeholder?: string;
  options: Option[];
  label: string;
}

function MobileModalFilterItem(props: ModalFilterItemProps) {
  const { fbt } = useI18n();
  const { onSelect, value, placeholder = fbt("Any"), options, label } = props;

  return (
    <ListGroup.Item className={`${styles.filterItem} mobile-filter-item`}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.modalDropdown)}
        onSelect={(value) => onSelect(value || undefined)}
      >
        <Dropdown.Toggle>
          <div className={styles.displayValue}>
            <div className={styles.label}>{label}</div>
            <Spacer size={8} />
            {value ? (
              <div>{options.find((o) => o.value === value)?.label}</div>
            ) : (
              <div className={styles.placeholder}>{placeholder}</div>
            )}
          </div>
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {value && (
          <div className={styles.clearBtn} onClick={() => onSelect(undefined)}>
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={option.value === value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </ListGroup.Item>
  );
}

interface MobileModalDateTimeFilterItemProps {
  onChange: (value: MeetingRoomDateRange) => void;
  value: MeetingRoomDateRange;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
}

function MobileModalDateTimeFilterItem({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
}: MobileModalDateTimeFilterItemProps) {
  const { fbt, locale } = useI18n();
  const displayValue = formatMeetingRoomDateRange(value, fbt, locale, "");

  return (
    <ListGroup.Item className={`${styles.filterItem} mobile-filter-item`}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.modalDropdown)}
      >
        <Dropdown.Toggle>
          <div className={styles.displayValue}>
            <div className={styles.label}>{label}</div>
            <Spacer size={8} />
            {displayValue ? (
              <div>{displayValue}</div>
            ) : (
              <div className={styles.placeholder}>{fbt("Any")}</div>
            )}
          </div>
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {displayValue && (
          <div
            className={styles.clearBtn}
            onClick={() =>
              onChange({
                date: undefined,
                startTime: undefined,
                endTime: undefined,
              })
            }
          >
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu>
          <div>
            <DateRangePicker
              pickingRange={PickingRange.OneDate}
              value={{
                startDate: value.date ? parseISO(value.date) : undefined,
              }}
              onChange={(value) =>
                onChange({
                  ...value,
                  date: toDay(value.startDate),
                })
              }
            />
            <div className="px-3 pb-3 d-flex">
              <TimePicker
                value={value.startTime}
                placeholder={fbt("Start time")}
                startTime={openAt}
                endTime={closedAt}
                step={timeSlotIncrement}
                onChange={(startTime) => onChange({ ...value, startTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.date}
              />
              <Spacer size={30} direction="horizontal" />
              <TimePicker
                value={value.endTime}
                step={timeSlotIncrement}
                placeholder={fbt("End time")}
                startTime={addHours(value.startTime || openAt || "0800", 1)}
                endTime={closedAt}
                onChange={(endTime) => onChange({ ...value, endTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.startTime}
              />
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </ListGroup.Item>
  );
}
